import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { ArrowUp, ArrowDown, Check, Loader, Zap, HardDrive, Users, Cpu } from 'lucide-react';

const PLAN_COLORS = {
  Dirt:       'from-yellow-900/20 to-gray-900 border-yellow-700/30',
  Stone:      'from-gray-700/20 to-gray-900 border-gray-600/30',
  Iron:       'from-blue-900/20 to-gray-900 border-blue-700/30',
  Diamond:    'from-cyan-900/20 to-gray-900 border-cyan-700/30',
  Netherite:  'from-purple-900/20 to-gray-900 border-purple-700/30',
};

const PlanUpgrade = ({ server, onUpgraded }) => {
  const [plans, setPlans] = useState([]);
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(null);
  const [daysRemaining, setDaysRemaining] = useState(30);

  useEffect(() => {
    Promise.all([
      api.get('/plans'),
      api.get('/balance'),
    ]).then(([plansRes, balanceRes]) => {
      setPlans(plansRes.data.plans || []);
      setBalance(balanceRes.data.balance || 0);

      // Calculate days remaining from upcoming renewals
      const renewal = balanceRes.data.upcoming_renewals?.find(r => r.server_name === server?.name);
      if (renewal?.next_due_date) {
        const days = Math.max(0, Math.ceil((new Date(renewal.next_due_date) - new Date()) / (1000 * 60 * 60 * 24)));
        setDaysRemaining(days);
      }
    }).catch(() => toast.error('Failed to load plans'))
      .finally(() => setLoading(false));
  }, []);

  const handleUpgrade = async (newPlan) => {
    const isUpgrade = newPlan.price_monthly > server.current_price;
    const action = isUpgrade ? 'upgrade' : 'downgrade';
    if (!window.confirm(`Are you sure you want to ${action} to the ${newPlan.name} plan?`)) return;

    setUpgrading(newPlan.id);
    try {
      const { data } = await api.post(`/servers/${server.id}/upgrade`, { new_plan_id: newPlan.id });
      toast.success(data.message);
      if (onUpgraded) onUpgraded(newPlan);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to change plan');
    } finally {
      setUpgrading(null);
    }
  };

  if (loading) return (
    <div className="flex justify-center py-8">
      <Loader className="w-6 h-6 text-green-400 animate-spin" />
    </div>
  );

  const currentPlanPrice = parseFloat(server.price_monthly || 0);
  const cycledays = 30;
  const proratedFraction = Math.min(1, daysRemaining / cycledays);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-bold text-white">Change Plan</h3>
          <p className="text-gray-500 text-sm mt-0.5">Upgrades are charged from your balance. Downgrades are credited back.</p>
        </div>
        <div className="bg-gray-800 border border-gray-700 rounded-xl px-4 py-2 text-sm">
          <span className="text-gray-400">Balance: </span>
          <span className="text-green-400 font-bold">€{parseFloat(balance).toFixed(2)}</span>
        </div>
      </div>

      {daysRemaining < 30 && (
        <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-3 text-sm text-blue-400">
          ℹ️ Prices shown are prorated — you have <strong>{daysRemaining} days</strong> remaining in your billing cycle ({Math.round(proratedFraction * 100)}% of the month).
        </div>
      )}

      <div className="grid grid-cols-1 gap-3">
        {plans.map((plan) => {
          const planPrice = parseFloat(plan.price_monthly);
          const isCurrent = plan.id === server.plan_id;
          const isUpgrade = planPrice > currentPlanPrice;
          const isDowngrade = planPrice < currentPlanPrice;
          const fullDiff = Math.abs(planPrice - currentPlanPrice);
          const proratedDiff = (fullDiff * proratedFraction).toFixed(2);
          const canAfford = isDowngrade || parseFloat(balance) >= fullDiff * proratedFraction;
          const gradientClass = PLAN_COLORS[plan.name] || 'from-gray-800 to-gray-900 border-gray-700';

          return (
            <div
              key={plan.id}
              className={`bg-gradient-to-r ${gradientClass} border rounded-2xl p-4 flex items-center gap-4 ${
                isCurrent ? 'opacity-60' : ''
              }`}
            >
              {/* Plan info */}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-bold text-white">{plan.name}</span>
                  {isCurrent && (
                    <span className="text-xs bg-green-500/20 text-green-400 border border-green-500/20 px-2 py-0.5 rounded-full">
                      Current
                    </span>
                  )}
                  {!isCurrent && isUpgrade && (
                    <span className="text-xs bg-blue-500/20 text-blue-400 border border-blue-500/20 px-2 py-0.5 rounded-full flex items-center gap-1">
                      <ArrowUp className="w-3 h-3" /> Upgrade
                    </span>
                  )}
                  {!isCurrent && isDowngrade && (
                    <span className="text-xs bg-yellow-500/20 text-yellow-400 border border-yellow-500/20 px-2 py-0.5 rounded-full flex items-center gap-1">
                      <ArrowDown className="w-3 h-3" /> Downgrade
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-4 text-xs text-gray-400">
                  <span className="flex items-center gap-1"><Cpu className="w-3 h-3" />{plan.ram_mb / 1024}GB RAM</span>
                  <span className="flex items-center gap-1"><HardDrive className="w-3 h-3" />{plan.disk_mb / 1024}GB</span>
                  <span className="flex items-center gap-1"><Users className="w-3 h-3" />{plan.player_slots} slots</span>
                </div>
              </div>

              {/* Price */}
              <div className="text-right">
                <div className="font-black text-white">€{planPrice.toFixed(2)}</div>
                <div className="text-gray-500 text-xs">/month</div>
                {!isCurrent && (
                  <div className={`text-xs mt-0.5 font-medium ${isUpgrade ? 'text-red-400' : 'text-green-400'}`}>
                    {isUpgrade ? `-€${proratedDiff}` : `+€${proratedDiff}`}
                    <span className="text-gray-600 font-normal ml-1">({daysRemaining}d left)</span>
                  </div>
                )}
              </div>

              {/* Action button */}
              {!isCurrent && (
                <button
                  onClick={() => handleUpgrade(plan)}
                  disabled={upgrading === plan.id || !canAfford}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-colors flex-shrink-0 ${
                    !canAfford
                      ? 'bg-gray-800 text-gray-600 border border-gray-700 cursor-not-allowed'
                      : isUpgrade
                      ? 'bg-blue-500 hover:bg-blue-400 text-white'
                      : 'bg-yellow-500/20 hover:bg-yellow-500/30 border border-yellow-500/30 text-yellow-400'
                  }`}
                >
                  {upgrading === plan.id
                    ? <Loader className="w-4 h-4 animate-spin" />
                    : isUpgrade
                    ? <><ArrowUp className="w-4 h-4" /> Upgrade</>
                    : <><ArrowDown className="w-4 h-4" /> Downgrade</>
                  }
                </button>
              )}

              {isCurrent && (
                <div className="flex items-center gap-1.5 px-4 py-2 text-green-400 text-sm flex-shrink-0">
                  <Check className="w-4 h-4" /> Active
                </div>
              )}
            </div>
          );
        })}
      </div>

      {plans.some(p => parseFloat(p.price_monthly) > currentPlanPrice && parseFloat(balance) < Math.abs(parseFloat(p.price_monthly) - currentPlanPrice) * proratedFraction) && (
        <p className="text-gray-600 text-xs text-center">
          Some upgrades require more balance. <a href="/balance" className="text-green-400 hover:text-green-300">Top up here →</a>
        </p>
      )}
    </div>
  );
};

export default PlanUpgrade;
